import { useContext } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AuthContext } from 'src/context/auth.context';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { useTaskQueue } from 'src/api/rpc/hooks/tasks/useTaskQueue';
import {
  adjacentPagesRequested,
  recordNavigationFromPages,
  resolveListNeighbors,
  type RecordListNavigationModel,
  type RecordListTarget,
} from 'src/components/common/list-record-navigation';
import { taskListQueryPlan, taskNavigationSearch } from '../taskDetailNav';

/**
 * Previous/next for a task opened from `/tasks` or a task page. Hidden when
 * the task was opened from anywhere else, or it is no longer on that page of
 * the filtered list.
 */
export function useTaskListNavigation(taskId: string) {
  const search = useSearch({ from: '/authenticated/tasks/$taskId' });
  const { currentUser } = useContext(AuthContext);
  const types = useTaskTypes();
  const plan = taskListQueryPlan({
    search,
    types: types.data,
    typesLoaded: types.isSuccess,
    currentUserId: currentUser?.id,
  });
  const current = useTaskQueue(plan.current, plan.ready);
  const edges = resolveListNeighbors({
    currentId: taskId,
    idOf: (task) => task.id,
    page: plan.page,
    pageSize: plan.pageSize,
    total: current.total,
    current: { items: current.tasks, loaded: current.isSuccess },
  });
  const adjacent = adjacentPagesRequested(plan.ready, edges);
  const previous = useTaskQueue(plan.previous, adjacent.previous);
  const next = useTaskQueue(plan.next, adjacent.next);
  const model = recordNavigationFromPages({
    currentId: taskId,
    page: plan.page,
    pageSize: plan.pageSize,
    current: queueSlice(current),
    previous: queueSlice(previous),
    next: queueSlice(next),
  });
  const navigate = useNavigate();
  return taskNavigation(plan.ready, model, (target) => {
    void navigate({
      to: '/tasks/$taskId',
      params: { taskId: target.id },
      search: taskNavigationSearch(search, target.page),
      replace: true,
      resetScroll: false,
    });
  });
}

function taskNavigation(
  ready: boolean,
  model: RecordListNavigationModel | null,
  open: (target: RecordListTarget) => void,
) {
  if (!ready || !model) return null;
  return { model, open };
}

type QueueQuery = ReturnType<typeof useTaskQueue>;

function queueSlice(query: QueueQuery) {
  return {
    items: query.tasks,
    total: query.total,
    isSuccess: query.isSuccess,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
  };
}
